/**
 * rule-engine.ts — A tiny, framework-agnostic rules/capability engine.
 *
 * The goal is to keep feature code free of scattered, inline conditionals that
 * reference individual rule names (e.g. `if (isAdmin && groupManageable)`).
 * Instead, a component declares a *table* of named capabilities, each defined
 * declaratively by composing small named predicates (`Rule`s) that read from a
 * context bag of facts. At runtime the component asks the `RuleSet` for a
 * capability map (a plain `Record<string, boolean>`) and render/behaviour code
 * only ever consults those booleans — never the rules or facts themselves.
 *
 * This engine is intentionally generic: the context shape, fact keys, and
 * capability keys are all supplied by the consumer, so different components can
 * reuse the same engine with entirely different flows, keys, and values.
 *
 * Usage:
 *   type Ctx = { isAdmin: boolean; manageable: boolean };
 *   const isAdmin: Rule<Ctx> = fact('isAdmin', (c) => c.isAdmin);
 *   const manageable: Rule<Ctx> = fact('manageable', (c) => c.manageable);
 *   const rules = new RuleSet<Ctx>({
 *       canEdit: all(isAdmin, manageable),
 *       canView: any(isAdmin, manageable),
 *   });
 *   const caps = rules.capabilities({ isAdmin: true, manageable: false });
 *   // caps.canEdit === false, caps.canView === true
 */

/** A named boolean predicate evaluated against a context of facts. */
export interface Rule<C> {
    /** Stable identifier, useful for debugging/inspection. */
    readonly key: string;
    /** Evaluate the rule against a context, returning a boolean verdict. */
    evaluate(ctx: C): boolean;
}

/** A declarative map of capability name → rule that grants it. */
export type RuleTable<C> = Record<string, Rule<C>>;

/** The resolved verdicts for every capability in a table. */
export type CapabilityMap<T extends RuleTable<any>> = Record<keyof T, boolean>;

/** Wrap a predicate function as a named {@link Rule}. */
export function rule<C>(key: string, evaluate: (ctx: C) => boolean): Rule<C> {
    return { key, evaluate };
}

/**
 * A "fact" rule reads a single named truthy value from the context. This keeps
 * the mapping between context keys and rule names in one obvious place.
 */
export function fact<C>(key: keyof C & string, read?: (ctx: C) => unknown): Rule<C> {
    const reader = read ?? ((ctx: C) => (ctx as Record<string, unknown>)[key]);
    return rule<C>(key, (ctx) => Boolean(reader(ctx)));
}

/** True only when *every* supplied rule is true (logical AND). */
export function all<C>(...rules: Rule<C>[]): Rule<C> {
    return rule<C>(
        `all(${rules.map((r) => r.key).join(',')})`,
        (ctx) => rules.every((r) => r.evaluate(ctx))
    );
}

/** True when *any* supplied rule is true (logical OR). */
export function any<C>(...rules: Rule<C>[]): Rule<C> {
    return rule<C>(
        `any(${rules.map((r) => r.key).join(',')})`,
        (ctx) => rules.some((r) => r.evaluate(ctx))
    );
}

/** Negates a rule (logical NOT). */
export function not<C>(inner: Rule<C>): Rule<C> {
    return rule<C>(`not(${inner.key})`, (ctx) => !inner.evaluate(ctx));
}

/** A rule that is always true (useful as a default/placeholder). */
export function always<C>(): Rule<C> {
    return rule<C>('always', () => true);
}

/** A rule that is always false. */
export function never<C>(): Rule<C> {
    return rule<C>('never', () => false);
}

/**
 * Evaluates a declared table of capabilities against a context. Consumers ask
 * for `can(name, ctx)` for a single verdict or `capabilities(ctx)` for the
 * whole map, then drive UI/behaviour off the resulting booleans.
 */
export class RuleSet<T extends RuleTable<any>> {
    constructor(private readonly table: T) {}

    /** The capability names declared in this set. */
    get keys(): Array<keyof T> {
        return Object.keys(this.table) as Array<keyof T>;
    }

    /** Evaluate a single capability by name. Unknown names resolve to false. */
    can<C>(name: keyof T, ctx: C): boolean {
        const r = this.table[name];
        return r ? r.evaluate(ctx) : false;
    }

    /** Evaluate every capability, returning a plain boolean map. */
    capabilities<C>(ctx: C): CapabilityMap<T> {
        const out = {} as CapabilityMap<T>;
        for (const name of this.keys) {
            out[name] = this.table[name].evaluate(ctx);
        }
        return out;
    }
}
